import React from 'react';
import { Shield, Mail, Phone, MapPin } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-16 h-16 mx-auto mb-6 text-[#FDB913]" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-xl opacity-90">
            Last Updated: January 26, 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
            
            {/* Introduction */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The Lokal Winery ("we," "us," or "our") is committed to protecting your privacy and personal information. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit 
                our website <a href="https://thelokal.ca" className="text-[#FDB913] hover:underline">thelokal.ca</a>, 
                use our services, or interact with us.
              </p>
              <p className="text-gray-700 leading-relaxed">
                This policy complies with the Personal Information Protection and Electronic Documents Act (PIPEDA) 
                and other applicable Canadian privacy legislation.
              </p>
            </div>

            {/* Information We Collect */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">2.1 Personal Information</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may collect the following personal information:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Name (first and last name)</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Address and postal code</li>
                <li>Appointment booking information</li>
                <li>Wine preferences and purchase history</li>
                <li>Payment information (processed through secure third-party providers)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">2.2 Automatically Collected Information</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you visit our website, we may automatically collect:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>IP address and device information</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Pages visited and time spent on our website</li>
                <li>Referring website addresses</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </div>

            {/* How We Use Your Information */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use your personal information for the following purposes:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>Service Delivery:</strong> To process your wine-making orders, schedule appointments, and provide customer service</li>
                <li><strong>Communication:</strong> To send you booking confirmations, updates, and respond to inquiries</li>
                <li><strong>Marketing:</strong> To send newsletters, promotions, and special offers (with your consent)</li>
                <li><strong>Loyalty Program:</strong> To manage our Sip & Save rewards program</li>
                <li><strong>Website Improvement:</strong> To analyze usage patterns and improve our services</li>
                <li><strong>Legal Compliance:</strong> To comply with legal obligations and protect our rights</li>
                <li><strong>Business Operations:</strong> To maintain records and conduct business analytics</li>
              </ul>
            </div>

            {/* Disclosure of Information */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Disclosure of Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information with:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>Service Providers:</strong> Third-party vendors who assist with payment processing, email marketing, 
                website hosting, and analytics</li>
                <li><strong>Legal Authorities:</strong> When required by law, court order, or to protect our legal rights</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, sale, or acquisition of our business</li>
                <li><strong>With Your Consent:</strong> When you have provided explicit consent for specific purposes</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                All third-party service providers are contractually required to maintain the confidentiality and security 
                of your personal information.
              </p>
            </div>

            {/* Consent */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Consent</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                By providing your personal information, you consent to its collection, use, and disclosure as described 
                in this Privacy Policy. You may withdraw your consent at any time by contacting us, subject to legal or 
                contractual restrictions.
              </p>
              <p className="text-gray-700 leading-relaxed">
                For marketing communications, you can opt-out by clicking the "unsubscribe" link in any email we send or 
                by contacting us directly.
              </p>
            </div>

            {/* Data Security */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Security</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We implement appropriate technical and organizational security measures to protect your personal information 
                from unauthorized access, disclosure, alteration, or destruction. These measures include:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure server infrastructure</li>
                <li>Access controls and authentication</li>
                <li>Regular security assessments</li>
                <li>Employee training on data protection</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </div>

            {/* Data Retention */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your personal information only for as long as necessary to fulfill the purposes outlined in this 
                Privacy Policy, comply with legal obligations, resolve disputes, and enforce our agreements. When personal 
                information is no longer required, we will securely delete or anonymize it.
              </p>
            </div>

            {/* Your Rights */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Your Rights Under PIPEDA</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Under Canadian privacy law, you have the right to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>Access:</strong> Request access to your personal information we hold</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Withdrawal:</strong> Withdraw consent for specific uses of your information</li>
                <li><strong>Complaint:</strong> File a complaint with the Office of the Privacy Commissioner of Canada</li>
                <li><strong>Explanation:</strong> Request an explanation of how your information is being used</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                To exercise these rights, please contact us using the information provided below.
              </p>
            </div>

            {/* Cookies */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use cookies and similar technologies to enhance your browsing experience, analyze website traffic, and 
                personalize content. You can control cookies through your browser settings, but disabling cookies may affect 
                website functionality.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Types of cookies we use include:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mt-2">
                <li><strong>Essential Cookies:</strong> Required for website operation</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
                <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements</li>
              </ul>
            </div>

            {/* Third-Party Links */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Third-Party Websites</h2>
              <p className="text-gray-700 leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices 
                or content of these external sites. We encourage you to review their privacy policies before providing 
                any personal information.
              </p>
            </div>

            {/* Children's Privacy */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our services are intended for individuals who are of legal drinking age in Ontario (19 years or older). 
                We do not knowingly collect personal information from individuals under 19. If we become aware that we 
                have collected information from someone under 19, we will delete it promptly.
              </p>
            </div>

            {/* Changes to Privacy Policy */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. 
                We will notify you of material changes by posting the updated policy on our website with a new "Last Updated" 
                date. Your continued use of our services after changes constitutes acceptance of the updated policy.
              </p>
            </div>

            {/* Contact Information */}
            <div className="bg-gray-50 rounded-lg p-6 border-2 border-[#FDB913]/30">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have questions, concerns, or requests regarding this Privacy Policy or your personal information, 
                please contact us:
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#FDB913] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">The Lokal Winery</p>
                    <p className="text-gray-700">6 Water St, Fenelon Falls, ON K0M 1N0</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#FDB913] flex-shrink-0" />
                  <a href="tel:+17058871864" className="text-gray-700 hover:text-[#FDB913]">
                    (705) 887-1864
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#FDB913] flex-shrink-0" />
                  <a href="mailto:info@thelokal.ca" className="text-gray-700 hover:text-[#FDB913]">
                    info@thelokal.ca
                  </a>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Office of the Privacy Commissioner of Canada:</strong><br />
                If you are not satisfied with our response to your privacy concerns, you may contact the Office of 
                the Privacy Commissioner of Canada at <a href="https://www.priv.gc.ca" className="text-[#FDB913] hover:underline" target="_blank" rel="noopener noreferrer">www.priv.gc.ca</a> or 
                call 1-800-282-1376.
              </p>
            </div>

            {/* Governing Law */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                This Privacy Policy is governed by and construed in accordance with the laws of the Province of Ontario 
                and the federal laws of Canada applicable therein.
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
